import { jsx, jsxs } from "react/jsx-runtime";
import { forwardRef, useRef, useMemo, useImperativeHandle, useEffect } from "react";
import PropTypes from "prop-types";
import { useId } from "@reach/auto-id";
import "lodash/fp";
import "styled-components";
import { useControlledState } from "../../hooks/use-controlled-state.es.js";
import "../../contexts/display-mode-context/display-mode-context.es.js";
import "../../contexts/global-edit-context/global-edit-context.es.js";
import "../../contexts/toast-context/toast-context.es.js";
import "../../utils/is-valid-ref.es.js";
import "../../hooks/use-horizontal-scroll.es.js";
import "react-use-measure";
import "@juggle/resize-observer";
import "@reach/utils";
import "@reach/rect";
import "compute-scroll-into-view";
import { Fieldset } from "../fieldset/fieldset.es.js";
import { Message } from "../message/message.es.js";
import { Stack } from "../stack/stack.es.js";
import { radioContext } from "./radio-context.es.js";
const { Provider } = radioContext;
const propTypes = {
  autoFocus: PropTypes.bool,
  children: PropTypes.node.isRequired,
  disabled: PropTypes.bool,
  hideLegend: PropTypes.bool,
  legend: PropTypes.string.isRequired,
  message: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  onChange: PropTypes.func,
  tone: PropTypes.oneOf(["critical", "neutral"]),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};
const defaultProps = {
  autoFocus: false,
  disabled: false,
  hideLegend: false,
  message: void 0,
  onChange: void 0,
  tone: "neutral",
  value: void 0
};
const RadioGroup = forwardRef(
  ({
    autoFocus,
    children,
    disabled,
    legend,
    hideLegend,
    message,
    onChange,
    tone,
    value,
    ...restProps
  }, forwardedRef) => {
    const ref = useRef();
    const id = useId();
    const [selectedValue, setValue] = useControlledState(value, onChange);
    const context = useMemo(
      () => ({
        allDisabled: disabled,
        autoFocus,
        name: `radio-group-${id}`,
        selectedValue,
        setValue
      }),
      [autoFocus, disabled, id, selectedValue, setValue]
    );
    useImperativeHandle(forwardedRef, () => ref.current);
    useEffect(() => {
      if (ref.current && autoFocus) {
        const input = ref.current.querySelector(
          value ? `input[value="${value}"]:enabled` : "input:enabled"
        );
        if (input) {
          input.focus();
        }
      }
    }, []);
    return /* @__PURE__ */ jsx(Provider, { value: context, children: /* @__PURE__ */ jsxs(Stack, { space: "xsmall", children: [
      /* @__PURE__ */ jsx(
        Fieldset,
        {
          ref,
          "aria-describedby": message ? `radio-group-${id}__message` : void 0,
          "aria-label": hideLegend ? legend : void 0,
          disabled,
          hideLegend,
          legend,
          role: "radiogroup",
          ...restProps,
          children
        }
      ),
      message && /* @__PURE__ */ jsx(Message, { id: `radio-group-${id}__message`, tone, children: message })
    ] }) });
  }
);
RadioGroup.propTypes = propTypes;
RadioGroup.defaultProps = defaultProps;
export {
  RadioGroup
};
